import { isoDate, startOfDay, addDays } from './format';

export type Granularity = 'day' | 'week' | 'month';

export function pickGranularity(days: number): Granularity {
  if (days <= 31) return 'day';
  if (days <= 120) return 'week';
  return 'month';
}

export interface TimeBucket {
  key: string;
  start: Date;
  end: Date;
  label: string;
}

export function buildBuckets(from: Date, to: Date, granularity: Granularity): TimeBucket[] {
  const buckets: TimeBucket[] = [];
  if (granularity === 'day') {
    let cur = startOfDay(from);
    const end = startOfDay(to);
    while (cur <= end) {
      const next = addDays(cur, 1);
      buckets.push({
        key: isoDate(cur),
        start: new Date(cur),
        end: new Date(next.getTime() - 1),
        label: isoDate(cur),
      });
      cur = next;
    }
  } else if (granularity === 'week') {
    let cur = startOfDay(from);
    const end = startOfDay(to);
    while (cur <= end) {
      const next = addDays(cur, 7);
      buckets.push({
        key: isoDate(cur),
        start: new Date(cur),
        end: new Date(Math.min(next.getTime() - 1, end.getTime())),
        label: isoDate(cur),
      });
      cur = next;
    }
  } else {
    let cur = new Date(from.getFullYear(), from.getMonth(), 1);
    const end = new Date(to.getFullYear(), to.getMonth(), 1);
    while (cur <= end) {
      const next = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
      buckets.push({
        key: cur.getFullYear() + '-' + String(cur.getMonth() + 1).padStart(2, '0'),
        start: new Date(cur),
        end: new Date(next.getTime() - 1),
        label: isoDate(cur),
      });
      cur = next;
    }
  }
  return buckets;
}

export function aggregateByBucket<T>(
  items: T[],
  getDate: (i: T) => Date | null | undefined,
  getValue: (i: T) => number,
  buckets: TimeBucket[],
): number[] {
  const result = new Array(buckets.length).fill(0);
  if (buckets.length === 0) return result;
  const bucketMap = new Map<string, number>();
  for (let i = 0; i < buckets.length; i++) {
    bucketMap.set(buckets[i]!.key, i);
  }
  for (const it of items) {
    const d = getDate(it);
    if (!d) continue;
    const dd = d instanceof Date ? d : new Date(d);
    if (isNaN(dd.getTime())) continue;
    const key = bucketKey(dd, buckets);
    if (key === null) continue;
    result[key] += getValue(it);
  }
  return result;
}

function bucketKey(d: Date, buckets: TimeBucket[]): number | null {
  for (let i = 0; i < buckets.length; i++) {
    if (d >= buckets[i]!.start && d <= buckets[i]!.end) return i;
  }
  return null;
}
